<template>
  <LoadingDiv v-if="!isReady" />
  
  <template v-else>
    <section class="tariff-upload-shell">
      <p class="tariff-upload-note is-success" v-if="success">{{ success }}</p>
      <p class="tariff-upload-note is-error" v-if="error">{{ error }}</p>

      <form @submit.prevent="handleSubmit" class="tariff-upload-form">
        <div class="tariff-upload-card">
          <div>
            <label class="active teal-text">Replacement File</label>
            <div class="file-field input-field tariff-file-field">
              <div class="btn grey darken-1">
                <span>Excel File</span>
                <input type="file" @change="handleFile" />
              </div>
              <div class="file-path-wrapper">
                <input class="file-path" v-model="inputFile" type="text" />
              </div>
            </div>
            <small v-if="fileError" class="red-text text-darken-2">{{ fileError }}</small>
          </div>

          <div>
            <label class="active teal-text">Lowest Plan Band</label>
            <div class="input-field tariff-plan-field">
              <select v-model="plan">
                <option value="" disabled selected>Select HMO Plan</option>
                <option v-for="plan in plans" :key="plan.id" :value="plan.id">
                  {{ plan.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <p class="tariff-upload-note is-error" v-if="uploadError">{{ uploadError }}</p>

        <div class="tariff-upload-submit">
          <div>
            <span>Replace provider tariff</span>
            <small>Upload an Excel tariff file and attach it to the selected plan band.</small>
          </div>
          <button class="waves-effect waves-light btn" :class="{
            disabled: !inputFile || !plan || isSubmitting,
          }">
            <i class="material-icons right">upload</i>{{ isSubmitting ? `Uploading (${uploadProgress})` : "Upload Tariff" }}
          </button>
        </div>
      </form>
    </section>
  </template>
</template>

<script>
import { onMounted, onUpdated, ref, computed, watchEffect } from "vue";
import { useStore } from "vuex";
import useStorage from "@/composables/useStorage";
import getItems from "@/composables/getItems";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { adminAPI } from '@/laravel/config';
import nativeUi from "@/ui/native-ui";

export default {
  name: "AddTariffComponent",
  components: { LoadingDiv },
  props: ["providerId", "planId"],
  emits: ["reload"],
  setup(props, context) {
    const store = useStore();
    const response = ref(null);
    const file = ref(null);
    const inputFile = ref(null);
    const fileError = ref(null);
    const plan = ref(props.planId);
    const success = ref(null);
    const isReady = ref(false);
    const isSubmitting = ref(false);
    const error = ref(null);
    const types = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    // storage
    const { uploadFile, uploadProgress, uploadError, data, completed } =
      useStorage();

    // get function
    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // page is ready
        isReady.value = true;
      })();
    };

    // drop down
    onMounted(() => {
      // activate select field
      const elemsSelect = document.querySelectorAll("select");
      nativeUi.FormSelect.init(elemsSelect, {});
    });

    // drop down
    onUpdated(() => {
      // activate select field
      const elemsSelect = document.querySelectorAll("select");
      nativeUi.FormSelect.init(elemsSelect, {});
    });

    // starter
    onMounted(() => {
      // get plans
      getItemsFromApi("plan/private");
    });

    // clear
    const clearForm = () => {
      file.value = null; //clear file value
      inputFile.value = null; // Clear the file input value
      plan.value = null;
      isSubmitting.value = false;
      uploadProgress.value = "0%";
    };

    // handle file
    const handleFile = (e) => {
      const selected = e.target.files[0];
      if (selected && types.includes(selected.type)) {
        file.value = selected;
        inputFile.value = file.value.name;
        fileError.value = null;
      } else {
        file.value = null;
        inputFile.value = null;
        fileError.value = "Please select an Excel file (.XLSX, XLS)";
      }
    };

    // submit
    const handleSubmit = () => {
      if (file.value && plan.value) {
        // Start Uploading
        isSubmitting.value = true;
        uploadError.value = null;

        // Cloud Function
        const fx = "tariffLoader";

        // Start
        let schedule = {
          plan_id: plan.value,
          type: "c", // c=customized, s=standard
        };

        // Round up
        const cloudInput = {
          schedule: schedule,
          provider: props.providerId,
          newProvider: null,
          endPoint: `${adminAPI}/schedule/tariffs/bulk`,
          authToken: store.state?.user?.token || null,
        };

        // upload
        uploadFile(file.value, fx, cloudInput);
      } else {
        // error
        clearForm();
        // error
        error.value = "Sorry, something went wrong!";
      }
    };

    // watch tariff upload
    watchEffect(() => {
      if (completed.value) {
        // clear
        clearForm();

        // data.value.succ.Success, data.value.succ.inserted_rows, data.value.succ.Error
        success.value = `New tariff: ${data.value.succ.inserted_rows} items added successfully!`;

        // tariff update
        context.emit("reload", true);
      }
      if (uploadError.value) {
        // clear
        clearForm();
      }
    });

    // output results
    const plans = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    return {
      isReady,
      isSubmitting,
      handleFile,
      handleSubmit,
      completed,
      error,
      uploadProgress,
      fileError,
      uploadError,
      plan,
      success,
      file,
      inputFile,
      plans,
    };
  },
};
</script>

<style scoped>
.tariff-upload-shell,
.tariff-upload-form {
  display: grid;
  gap: 14px;
}

.tariff-upload-note {
  border-radius: 6px;
  font-weight: 700;
  margin: 0;
  max-width: max-content;
  padding: 9px 12px;
}

.tariff-upload-note.is-success {
  background: #e8f5e9;
  color: #1b5e20;
}

.tariff-upload-note.is-error {
  background: #ffebee;
  color: #b71c1c;
}

.tariff-upload-card,
.tariff-upload-submit {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.tariff-upload-form {
  background: #ffffff;
  border: 1px solid #e3eaed;
  border-radius: 6px;
  box-shadow: 0 18px 32px -30px rgba(38, 50, 56, 0.72);
  gap: 0;
  overflow: hidden;
}

.tariff-upload-card {
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1.1fr) minmax(240px, 0.9fr);
  padding: 20px;
}

.tariff-upload-card label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.tariff-file-field,
.tariff-plan-field {
  margin: 0;
}

.tariff-file-field .btn {
  border-radius: 6px;
  box-shadow: none;
  font-weight: 700;
  text-transform: none;
}

.tariff-upload-submit {
  align-items: center;
  background: #fafafa;
  border-top: 1px solid #eef3f5;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 16px 18px;
}

.tariff-upload-submit span {
  color: #263238;
  display: block;
  font-weight: 700;
}

.tariff-upload-submit small {
  color: #607d8b;
  display: block;
  margin-top: 4px;
}

.tariff-upload-submit button {
  background: #00897b;
  border-radius: 6px;
  box-shadow: none;
  flex: 0 0 auto;
  font-weight: 700;
  text-transform: none;
}

@media only screen and (max-width: 700px) {
  .tariff-upload-card {
    grid-template-columns: 1fr;
  }

  .tariff-upload-submit {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
